import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; // Import useParams
import Select from 'react-select';
import { AuthContext } from '../../context/AuthContext';
import { addDemoAccount, addDemoAccountFinancial, fetchUserProfile, sendnewRealAccountReq } from '../../api/profile';

import InputField from '../common/InputField';
import { Button } from '../common/Button';
import LoadingSpinner from '../common/Loader';

const AccountCreationScreen = () => {
    const navigate = useNavigate();
    const { type } = useParams(); // Extract the `type` parameter from the URL
    const { user } = useContext(AuthContext);
    const [platform, setPlatform] = useState('MT');
    const [currency, setCurrency] = useState('USD');
    const [reason, setReason] = useState('');
    const [account_type, setAccountType] = useState(type === 'demo' ? 'Standard' : '');
    const [userProfile, setUserProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(false); // Loading state

   // console.log('Type from URL:', type); // Debugging statement

    const accountTierOptions = [
        { value: 'Standard', label: 'Standard' },
        { value: 'Premium', label: 'Premium' },
        { value: 'VIP', label: 'VIP' },
    ];

    const platformOptions = [
        { value: 'XSiDER', label: 'XSiDER' },
        { value: 'Other', label: 'Other' },
    ];

    const currencyOptions = [
        { value: 'USD', label: 'USD' },
    ];

    useEffect(() => {
        if (type === 'demo' && user) {
            fetchUserProfile(user.user_id)
                .then((profile) => setUserProfile(profile))
                .catch((error) => console.error('Error fetching user profile:', error));
        }
    }, [type, user]);

    const handleSubmit = async () => {
        if (!user) {
            alert('User not authenticated. Please log in.');
            return;
        }

        setIsLoading(true); // Start loading

        // Generate account number starting with "D-" for demo accounts
        const account_number = type === 'demo'
            ? `D-${Math.floor(Math.random() * 1000000)}`
            : `A-${Math.floor(Math.random() * 1000000)}`;

        let accountData;
        if (type === 'demo') {
            if (!userProfile) {
                alert('User profile data not available. Please try again.');
                setIsLoading(false); // Stop loading
                return;
            }

            accountData = {
                user_id: user.user_id,
                account_type: 'Demo',
                account_number: account_number,
                account_status: 'Active', // Default status for demo accounts
                account_mode: 'Demo', // Default mode for demo accounts
            };
        } else if (type === 'real') {
            accountData = {
                user_id: user.user_id,
                account_type: account_type,
                account_number: account_number,
                account_status: 'Pending', // Default status for real accounts
                account_mode: 'Real', // Default mode for real accounts
                platform: platform,
                currency: currency,
                reason: reason,
            };
        }

        const accountFinancialData = {
            account_id: account_number,
            userId: user.user_id,
            credit: 0.0,
            withdrawal_amount: 0.0,
            deposit: 0.0,
            leverage: type === 'demo' ? "1:200" : "1:100",
            equity: 0.00,
            balance: type === 'demo' ? 10000.00 : 0.00,
            margin: 25.00,
            platforms: platform,
            currency: currency,
        };


        try {
            if (type === 'demo') {
         //       console.log('Demo Account Data:', accountData);
                const demoAccountResponse = await addDemoAccount(accountData, user.access_token);
         //       console.log('Demo Account Response:', demoAccountResponse);
                if (!demoAccountResponse.error) {
                    const demoAccountFinancialResponse = await addDemoAccountFinancial(accountFinancialData);
                    if (!demoAccountFinancialResponse.error) {
                        navigate('/dashboard', { state: { message: 'Demo account created successfully!', isSuccess: true } });
                    }
                }
            } else if (type === 'real') {
         //       console.log('Real Account Data:', accountData);
                const emailResponse = await sendnewRealAccountReq(accountData);
                if (!emailResponse.error) {
                    navigate('/dashboard', { state: { message: 'Request for real account submitted. Please wait for approval.', isSuccess: true } });
                }
            }
        } catch (error) {
            console.error('Error creating account:', error);
            navigate('/dashboard', { state: { message: 'An error occurred while creating the account.', isSuccess: false } });
        } finally {
            setIsLoading(false); // Stop loading
        }
    };

    return (
        <>
            {isLoading && <LoadingSpinner />}
            <div className="flex justify-center items-center min-h-screen bg-gray-100">
                <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md relative">
                    <h1 className="text-2xl font-bold mb-6 text-center">
                        Create {type === 'demo' ? 'Demo' : 'Real'} Account
                    </h1>

                    {type === 'real' && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                            <Select
                                options={accountTierOptions}
                                value={accountTierOptions.find((option) => option.value === account_type)}
                                onChange={(selectedOption) => setAccountType(selectedOption.value)}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                        <Select
                            options={platformOptions}
                            value={platformOptions.find((option) => option.value === platform)}
                            onChange={(selectedOption) => setPlatform(selectedOption.value)}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                        <Select
                            options={currencyOptions}
                            value={currencyOptions.find((option) => option.value === currency)}
                            onChange={(selectedOption) => setCurrency(selectedOption.value)}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    {type === 'real' && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Account Creation</label>
                            <InputField
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Enter your reason"
                            />
                        </div>
                    )}

                    <Button onClick={handleSubmit} className="w-full mt-6" disabled={isLoading}>
                        Submit
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AccountCreationScreen;